'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useVersion } from '@/app/context/VersionContext';
import type { ModSelection } from '@/app/lib/gameData/modTypes';
import { EMPTY_MOD_SELECTION } from '@/app/lib/gameData/modTypes';
import {
  getModsForVersion,
  getModpacksForVersion,
  isModInModpack,
} from '@/app/lib/gameData/modRegistry';
import { useDialog } from '@/app/hooks/useDialog';
import { PRESS } from '@/app/components/ui/actionButtons';
import { MODAL_BACKDROP, MODAL_PANEL } from '@/app/components/ui/motion';

interface GameVersionModSelectorProps {
  dataOnly?: boolean;
  className?: string;
}

export default function GameVersionModSelector({
  dataOnly = false,
  className = '',
}: GameVersionModSelectorProps) {
  const {
    version,
    modSelection,
    setVersionAndMods,
    versionGroups,
    dataOnlyGroups,
    isPending,
  } = useVersion();

  const [isOpen, setIsOpen] = useState(false);

  // Draft state for modal — only applied on "Apply"
  const [draftVersion, setDraftVersion] = useState(version);
  const [draftMods, setDraftMods] = useState<ModSelection>(modSelection);

  // Search states
  const [versionSearch, setVersionSearch] = useState('');
  const [modpackSearch, setModpackSearch] = useState('');
  const [modSearch, setModSearch] = useState('');

  // Sync draft with actual state when modal opens
  const openModal = useCallback(() => {
    setDraftVersion(version);
    setDraftMods(modSelection);
    setVersionSearch('');
    setModpackSearch('');
    setModSearch('');
    setIsOpen(true);
  }, [version, modSelection]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const applySelection = useCallback(() => {
    setVersionAndMods(draftVersion, draftMods);
    setIsOpen(false);
  }, [draftVersion, draftMods, setVersionAndMods]);

  // Focus trap + Escape-to-close + focus restore, shared with other dialogs
  const modalRef = useDialog<HTMLDivElement>({ open: isOpen, onClose: closeModal });

  // Close on backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      closeModal();
    }
  }, [closeModal, modalRef]);

  // Data for the modal columns
  const groups = dataOnly ? dataOnlyGroups : versionGroups;

  const availableMods = useMemo(
    () => getModsForVersion(draftVersion),
    [draftVersion]
  );

  const availableModpacks = useMemo(
    () => getModpacksForVersion(draftVersion),
    [draftVersion]
  );

  // Reset mod selection when changing version (mods may not be compatible)
  const handleVersionChange = useCallback((newVersion: string) => {
    setDraftVersion(newVersion);
    // Clear mods that aren't available for the new version
    const newMods = getModsForVersion(newVersion);
    const newModIds = new Set(newMods.map(m => m.id));
    const newPacks = getModpacksForVersion(newVersion);
    const newPackIds = new Set(newPacks.map(p => p.id));

    setDraftMods(prev => ({
      modpack: prev.modpack && newPackIds.has(prev.modpack) ? prev.modpack : null,
      individualMods: prev.individualMods.filter(id => newModIds.has(id)),
    }));
  }, []);

  const handleModpackChange = useCallback((packId: string | null) => {
    setDraftMods(prev => ({
      ...prev,
      modpack: packId,
      // Remove individual mods that are included in the new modpack
      individualMods: packId
        ? prev.individualMods.filter(modId => !isModInModpack(modId, packId))
        : prev.individualMods,
    }));
  }, []);

  const handleModToggle = useCallback((modId: string) => {
    setDraftMods(prev => ({
      ...prev,
      individualMods: prev.individualMods.includes(modId)
        ? prev.individualMods.filter(id => id !== modId)
        : [...prev.individualMods, modId],
    }));
  }, []);

  // Filter helpers
  const filteredGroups = useMemo(() => {
    if (!versionSearch) return groups;
    const q = versionSearch.toLowerCase();
    return groups
      .map(g => ({
        ...g,
        versions: g.versions.filter(v =>
          v.name.toLowerCase().includes(q) || v.id.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.versions.length > 0);
  }, [groups, versionSearch]);

  const filteredModpacks = useMemo(() => {
    if (!modpackSearch) return availableModpacks;
    const q = modpackSearch.toLowerCase();
    return availableModpacks.filter(p =>
      p.displayName.toLowerCase().includes(q)
    );
  }, [availableModpacks, modpackSearch]);

  const filteredMods = useMemo(() => {
    if (!modSearch) return availableMods;
    const q = modSearch.toLowerCase();
    return availableMods.filter(m =>
      m.displayName.toLowerCase().includes(q) || m.id.toLowerCase().includes(q)
    );
  }, [availableMods, modSearch]);

  // Summary text for trigger button
  const summaryText = useMemo(() => {
    // Count total active mods (individual + modpack-included)
    let modCount = modSelection.individualMods.length;
    if (modSelection.modpack) {
      const pack = getModpacksForVersion(version).find(p => p.id === modSelection.modpack);
      if (pack) modCount += pack.modCount || Object.keys(pack.includedMods).length;
    }
    if (modCount === 0) return version;
    return `${version} + ${modCount} ${modCount === 1 ? 'Mod' : 'Mods'}`;
  }, [version, modSelection]);

  const hasModsSelected = modSelection.modpack !== null || modSelection.individualMods.length > 0;

  // 🚨 The modal is PORTALLED to <body>. Every tool header pins its control row (`max-lg:fixed …
  // z-30`) below lg, and a fixed element with a z-index creates a stacking context — so a modal
  // rendered here painted at an effective z-30 and the cookie banner (z-40) covered its Apply
  // button on every tool below 1024px. A portal puts it back at the top level.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Count active mods in draft
  const draftActiveModCount = draftMods.individualMods.length +
    (draftMods.modpack ? (() => { const p = getModpacksForVersion(draftVersion).find(p => p.id === draftMods.modpack); return p?.modCount || Object.keys(p?.includedMods ?? {}).length; })() : 0);

  // Edition / release-type tags for the footer's "Selected" label
  const draftVersionInfo = useMemo(
    () => groups.flatMap(g => g.versions).find(v => v.id === draftVersion),
    [groups, draftVersion]
  );

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={openModal}
        disabled={isPending}
        className={`inline-flex items-center gap-2.5 bg-zinc-800/60 border border-zinc-700/80
          rounded-xl px-3.5 py-2 transition-colors hover:border-zinc-600 hover:bg-zinc-800
          focus:outline-none focus:ring-2 focus:ring-violet-500/50
          disabled:opacity-50 disabled:cursor-wait cursor-pointer ${className}`}
      >
        <div className="flex items-center gap-2 text-zinc-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <span className="text-xs font-medium whitespace-nowrap hidden sm:inline">Game Version & Mod Selector</span>
        </div>
        <div className="w-px h-4 bg-zinc-700" />
        <span className="text-sm whitespace-nowrap">
          <span className="font-pixel text-xs text-zinc-200">{version}</span>
          {hasModsSelected && (() => {
            let modCount = modSelection.individualMods.length;
            if (modSelection.modpack) {
              const pack = getModpacksForVersion(version).find(p => p.id === modSelection.modpack);
              if (pack) modCount += pack.modCount || Object.keys(pack.includedMods).length;
            }
            return <span className="text-emerald-400"> + {modCount} {modCount === 1 ? 'Mod' : 'Mods'}</span>;
          })()}
        </span>
        {isPending && (
          <div className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
        )}
        <svg className="w-3.5 h-3.5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Modal */}
      {isOpen && mounted && createPortal(
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm ${MODAL_BACKDROP}`}
          onClick={handleBackdropClick}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="game-version-mod-selector-title"
            className={`bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-[95vw] max-w-[940px] max-h-[85dvh] flex flex-col ${MODAL_PANEL}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
              <h2 id="game-version-mod-selector-title" className="text-lg font-semibold text-zinc-100">Game Version & Mod Selector</h2>
              <button
                onClick={closeModal}
                aria-label="Close"
                className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 3-Column Content */}
            <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-800">
              {/* Left: MC Versions */}
              <div className="flex flex-col min-h-0">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="text-sm font-medium text-zinc-300 mb-2">Minecraft Version</h3>
                  <input
                    type="text"
                    value={versionSearch}
                    onChange={(e) => setVersionSearch(e.target.value)}
                    placeholder="Search versions..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-500
                      focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {filteredGroups.map((group) => (
                    <div key={group.label} className="mb-2">
                      <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider px-2 py-1">
                        {group.label}
                      </div>
                      {group.versions.map((v) => (
                        <button
                          key={v.id}
                          onClick={() => handleVersionChange(v.id)}
                          className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            draftVersion === v.id
                              ? 'bg-emerald-500/20 text-emerald-300 font-medium'
                              : 'text-zinc-300 hover:bg-zinc-800'
                          }`}
                        >
                          <span>{v.name}</span>
                          {v.isLatest && <span className="ml-1.5 text-[10px] text-emerald-500">(Latest)</span>}
                          {v.isExperimental && <span className="ml-1.5 text-[10px] text-amber-500">(Exp)</span>}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Middle: Modpacks */}
              <div className="flex flex-col min-h-0">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="text-sm font-medium text-zinc-300 mb-2">Modpacks</h3>
                  <input
                    type="text"
                    value={modpackSearch}
                    onChange={(e) => setModpackSearch(e.target.value)}
                    placeholder="Search modpacks..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-500
                      focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {/* Vanilla / None option */}
                  <button
                    onClick={() => handleModpackChange(null)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors mb-1 ${
                      draftMods.modpack === null
                        ? 'bg-emerald-500/20 text-emerald-300 font-medium'
                        : 'text-zinc-300 hover:bg-zinc-800'
                    }`}
                  >
                    <div className="font-medium">None (Vanilla)</div>
                    <div className="text-[11px] text-zinc-500">Base Minecraft only</div>
                  </button>

                  {filteredModpacks.length > 0 ? (
                    filteredModpacks.map((pack) => (
                      <button
                        key={pack.id}
                        onClick={() => handleModpackChange(pack.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors mb-1 ${
                          draftMods.modpack === pack.id
                            ? 'bg-purple-500/20 text-purple-300 font-medium'
                            : 'text-zinc-300 hover:bg-zinc-800'
                        }`}
                      >
                        <div className="font-medium">{pack.displayName}</div>
                        <div className="text-[11px] text-zinc-500">
                          v{pack.packVersion} &middot; {pack.itemCount.toLocaleString('en-US')} items &middot; {pack.modCount || Object.keys(pack.includedMods).length} mods
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="text-center py-8 text-zinc-500 text-sm">
                      No modpacks available for {draftVersion}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Individual Mods */}
              <div className="flex flex-col min-h-0">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="text-sm font-medium text-zinc-300 mb-2">Individual Mods</h3>
                  <input
                    type="text"
                    value={modSearch}
                    onChange={(e) => setModSearch(e.target.value)}
                    placeholder="Search mods..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-500
                      focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {filteredMods.length > 0 ? (
                    filteredMods.map((mod) => {
                      const includedInPack = draftMods.modpack
                        ? isModInModpack(mod.id, draftMods.modpack)
                        : false;
                      const isSelected = includedInPack || draftMods.individualMods.includes(mod.id);

                      return (
                        <button
                          key={mod.id}
                          onClick={() => !includedInPack && handleModToggle(mod.id)}
                          disabled={includedInPack}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors mb-1 flex items-start gap-3 ${
                            includedInPack
                              ? 'opacity-60 cursor-not-allowed'
                              : isSelected
                              ? 'bg-emerald-500/10 hover:bg-emerald-500/15'
                              : 'hover:bg-zinc-800'
                          }`}
                        >
                          {/* Checkbox */}
                          <div className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                            isSelected
                              ? 'bg-emerald-500 border-emerald-500'
                              : 'border-zinc-600'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={isSelected ? 'text-emerald-300 font-medium' : 'text-zinc-300'}>
                                {mod.displayName}
                              </span>
                              {includedInPack && (
                                <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                  In pack
                                </span>
                              )}
                              {(mod.modrinthSlug || mod.curseforgeSlug) && (
                                <span className="flex items-center gap-1.5 ml-auto flex-shrink-0">
                                  {mod.modrinthSlug && (
                                    <a
                                      href={`https://modrinth.com/mod/${mod.modrinthSlug}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-zinc-500 hover:text-green-400 transition-colors"
                                      title="View on Modrinth"
                                    >
                                      <svg className="w-4 h-4" viewBox="0 0 512 514" fill="currentColor">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M503.16 323.56C514.55 281.47 515.32 235.91 503.2 190.76C466.57 54.23 326.04 -26.8 189.33 9.78C83.81 38.02 11.39 128.07 0.69 230.47H43.99C54.29 147.33 113.74 74.73 199.75 51.71C306.05 23.26 415.13 80.67 453.17 181.38L411.03 192.65C391.64 145.8 352.57 111.45 306.3 96.82L298.56 140.66C335.09 154.13 364.72 184.5 375.56 224.91C391.36 283.8 361.94 344.14 308.56 369.17L320.09 412.16C390.25 383.21 432.4 310.3 422.43 235.14L464.41 223.91C468.91 252.62 467.35 281.16 460.55 308.07L503.16 323.56ZM321.99 504.22C185.27 540.8 44.75 459.77 8.11 323.24C3.84 307.31 1.17 291.33 0 275.46H43.27C44.36 287.37 46.47 299.35 49.68 311.29C53.04 323.8 57.45 335.75 62.79 347.07L101.38 323.92C98.13 316.42 95.39 308.6 93.21 300.47C69.17 210.87 122.41 118.77 212.13 94.76C229.13 90.21 246.23 88.55 262.93 89.54L271.15 89.12C254.35 85.07 236.94 84.04 219.5 86.04L227.24 42.19C245.96 40.58 265.15 41.79 284.09 46.07C390.39 74.49 453.17 181.38 411.03 192.65L369.26 204.02C365.44 192.25 359.97 181.23 353.04 171.2L311.71 182.39C324.66 199.1 332.77 220.09 333.75 243.04L290.28 254.72C289.71 237.84 283.57 221.94 273.29 209.41L231.44 220.73C241.72 231.26 248.8 245.19 251.07 261.24C256.26 296.93 234.16 330.37 200.12 341.11L211.83 384.65C237.64 376.94 258.66 361.41 272.69 341.66L313.76 330.19C295.56 360.77 266.3 384.63 230.36 396.18L242.05 439.64C299.33 421.49 342.82 375.09 358.67 318.82L399.87 307.69C392.25 355.34 365.72 398.76 326.32 428.91L338.36 473.53C386.89 439.14 421.64 388.37 436.21 330.79L477.8 319.79C474.44 335.21 469.64 350.16 463.48 364.38L504.38 380.13C510.56 365.56 515.2 350.21 518.1 334.39L503.16 323.56L477.8 319.79Z"/>
                                      </svg>
                                    </a>
                                  )}
                                  {mod.curseforgeSlug && (
                                    <a
                                      href={`https://www.curseforge.com/minecraft/mc-mods/${mod.curseforgeSlug}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-zinc-500 hover:text-orange-400 transition-colors"
                                      title="View on CurseForge"
                                    >
                                      <svg className="w-4 h-4" viewBox="0 0 438 406" fill="currentColor">
                                        <path d="M425.79 196.672L395.393 168.28H290.176C290.176 168.28 298.101 134.852 268.16 91.808C238.218 48.762 191.72 34.806 191.72 34.806L187.409 73.002C187.409 73.002 151.591 37.746 100.076 34.806C48.56 31.868 0 63.02 0 63.02L56.052 107.127C56.052 107.127 32.77 113.041 20.082 144.836C7.39 176.63 22.866 207.044 22.866 207.044H85.014C99.384 248.664 136.72 286.51 191.72 286.51C246.72 286.51 300.032 249.344 300.032 249.344L425.79 196.672Z"/>
                                      </svg>
                                    </a>
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-0.5">
                              v{mod.matchedVersion.modVersion} &middot; +{mod.matchedVersion.itemCount} items, +{mod.matchedVersion.blockCount} blocks
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-zinc-500 text-sm">
                      No individual mods available for {draftVersion}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-zinc-800">
              <div className="min-w-0" aria-live="polite">
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Selected</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="font-pixel text-base text-emerald-300">{draftVersion}</span>
                  <span className="text-xs text-zinc-400">{draftVersionInfo?.edition === 'bedrock' ? 'Bedrock' : 'Java'} Edition</span>
                  {draftVersionInfo?.isLatest && (
                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-emerald-500/15 text-emerald-400">Latest</span>
                  )}
                  {draftVersionInfo?.isExperimental && (
                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-500/15 text-amber-400">Experimental</span>
                  )}
                  {draftMods.modpack && (
                    <span className="text-xs text-zinc-400">+ <span className="text-purple-400">{availableModpacks.find(p => p.id === draftMods.modpack)?.displayName}</span></span>
                  )}
                  {draftMods.individualMods.length > 0 && (
                    <span className="text-xs text-zinc-400">+ <span className="text-emerald-400">{draftMods.individualMods.length} mod{draftMods.individualMods.length !== 1 ? 's' : ''}</span></span>
                  )}
                  {draftActiveModCount > 0 && (
                    <span className="text-xs text-zinc-500">({draftActiveModCount} total mod{draftActiveModCount !== 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-auto">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg hover:bg-zinc-800"
                >
                  Cancel
                </button>
                <button
                  onClick={applySelection}
                  className={`px-5 py-2 text-sm font-medium bg-emerald-600 text-emerald-950 rounded-lg
                    hover:bg-emerald-500 ${PRESS} focus:outline-none focus:ring-2 focus:ring-violet-500/50`}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
